

import { Badge, Button, Checkbox, Grid, Group, Select, Space, Text, ThemeIcon, Title, Tooltip } from '@mantine/core';
import { HitsPerPage, InstantSearch, Pagination, SearchBox, useHits, InstantSearchProps, useInstantSearch, Configure, useSortBy, useRefinementList, } from 'react-instantsearch';

import { TagBadge } from './tag_badge';
import FilterModal, { FilterSection } from './FilterModal';
import { FilterBadgeGroup } from './FilterBadges';
import TypesenseInstantSearchAdapter, { SearchClient } from "typesense-instantsearch-adapter";
import { forwardRef, useContext, useEffect, useImperativeHandle, useState } from 'react';
import { ConfigContext, GlobalDataContext, MyUserDataContext, PresenceContext } from '../contexts';
import DataTable, { TableColumn } from 'react-data-table-component';

import { useSessionStorage } from '@mantine/hooks';
import { CustomClaims, CustomerType, KYCStatusCode, Viewer, OTHER_ID_TYPE_LABELS, OtherIdType } from '../types';
import TypesenseInstantsearchAdapter from 'typesense-instantsearch-adapter';
import { HighlightedText } from '../helpers/common';
import { IconBuilding, IconEye, IconFileCheck, IconQuestionMark, IconShieldLock, IconUser } from '@tabler/icons-react';
import { CurrentViewers } from './current_viewers';



//typesense

interface InitialState {
    query?: string;
    page?: number;
}

// Define the interface for the ref
export interface RefreshButtonRef {
    refresh: () => void;
}


export const RefreshButton = forwardRef<RefreshButtonRef>((props, ref) => {
    const { refresh } = useInstantSearch();

    useImperativeHandle(ref, () => ({
        refresh
    }));

    return (
        <Button onClick={() => refresh()}>
            Refresh
        </Button>
    );
});




const TableDisplay = ({ indexName, setSelectedRows, selectedRows, renderTags, ActionsComponent, navigateDetails, queryValue, onTotalCustomersUpdate, clearTrigger, customerType, setCustomerType, ...props }: any) => {
    const { items, results } = useHits();
    const configContext = useContext(ConfigContext);
    const [openFilterModal, setOpenFilterModal] = useState<boolean>(false);
    const dataContext = useContext(GlobalDataContext);
    const userContext = useContext(MyUserDataContext);
    const userClaims = userContext.firebaseTokenResult?.claims.customClaims as CustomClaims;
    // Consent status is only relevant to KFH bank staff, whose access to customer data is gated on it
    const showConsent = userClaims?.["kfh_rm"] === true || userClaims?.["kfh_oper"] === true;


    onTotalCustomersUpdate(results?.nbHits);

    const { refine, } = useSortBy(
        {
            items: [
                { label: 'Created (asc)', value: `${indexName}/sort/created:asc` },
                { label: 'Created (desc)', value: `${indexName}/sort/created:desc` },
            ]


        }
    );

    const customerTypeFilterLabel = (item: { value: string }) =>
        item.value.charAt(0).toUpperCase() + item.value.slice(1);
    const customerTypeItems = customerType === "all" ? [] : [
        { value: customerType, label: customerTypeFilterLabel({ value: customerType }), count: 0, isRefined: true },
    ];
    const clearCustomerType = () => setCustomerType("all");

    const {
        items: nationalityItems,
        refine: refineNationality,

    } = useRefinementList({ ...props, attribute: "nationality", operator: "or", limit: 9999 });

    const {
        items: segmentItems,
        refine: refineSegment,

    } = useRefinementList({ ...props, attribute: "segment", operator: "or", limit: 9999 });

    const {
        items: tagsItems,
        refine: refineTags,

    } = useRefinementList({ ...props, attribute: "tags_object_arr.code", operator: "or", limit: 9999 });


    const {
        items: kycStatusItems,
        refine: refineKycStatus,

    } = useRefinementList({ ...props, attribute: "tags.kycStatus.code", operator: "or", limit: 9999 });

    const {
        items: consentItems,
        refine: refineConsent,

    } = useRefinementList({ ...props, attribute: "kfh_rm_access_consent", operator: "or", limit: 10 });

    const consentFilterLabel = (item: { value: string }) => item.value === "true" ? "Consent signed" : "Consent not signed";


    const handleChange = ({ selectedRows }: any) => {
        // You can set state or dispatch with something like Redux so we can use the retrieved data
        if (setSelectedRows) {
            setSelectedRows(selectedRows);
        }
    };
    // Conditional styles
    const conditionalRowStyles = [

        {
            when: (row: any) => !row.has_active_products,
            style: {
                backgroundColor: '#ffe6e6',
            },
        },

    ];

    const columns: TableColumn<any>[] = [
        {
            name: "Type",
            cell(row,) {

                let typeLabel: string | undefined;
                let TypeIcon = IconUser;

                if (row.customerType === "individual") {
                    typeLabel = "Individual";
                    TypeIcon = IconUser;
                } else if (row.customerType === "company") {
                    typeLabel = "Company";
                    TypeIcon = IconBuilding;
                } else if (row.customerType === "trust") {
                    typeLabel = "Trust";
                    TypeIcon = IconShieldLock;
                } else if (row.customerType === "other") {
                    typeLabel = "Other";
                    TypeIcon = IconQuestionMark;
                } else if (row.civilID || row.other_id) {
                    typeLabel = "Individual";
                    TypeIcon = IconUser;
                } else if (row.commercial_register) {
                    typeLabel = "Company";
                    TypeIcon = IconBuilding;
                }

                return <Group gap={2} wrap="nowrap">
                    {typeLabel &&
                        <Tooltip label={typeLabel} transitionProps={{ transition: 'scale', duration: 500 }}>
                            <ThemeIcon variant="white" color="blue">
                                <TypeIcon size={16} />
                            </ThemeIcon>
                        </Tooltip>
                    }
                    {showConsent && row.kfh_rm_access_consent &&
                        <Tooltip label="Consent agreement signed" transitionProps={{ transition: 'scale', duration: 500 }}>
                            <ThemeIcon variant="white" color="green">
                                <IconFileCheck size={16} />
                            </ThemeIcon>
                        </Tooltip>
                    }
                </Group>

            },
            // sortable: true,
            width: showConsent ? "100px" : "70px",
        },
        {
            name: <Tooltip label="Current Viewers" transitionProps={{ transition: 'scale', duration: 500 }}><IconEye size={16} /></Tooltip>,
            cell(row,) {

                return (
                    <CurrentViewers type="customers" id={row.id} />
                );
            },
            // sortable: true,
            width: "90px",
        },
        {
            name: 'Name',
            id: "name_en",
            sortable: true,
            selector: (row: any) => row.name_en,
            grow: 2,
            cell(row,) {


                const displayedLabel = `${row.name_en}`


                // const regex = new RegExp(`(${queryValue})`, 'gi'); // Case insensitive match
                // const fuzzyRegex = new RegExp(`(${queryValue?.split('').join('.*?')})`, 'gi'); // Matches characters in any order with any characters in between

                // const parts: string[] = displayedLabel?.split(fuzzyRegex) || [];
                return <HighlightedText displayedLabel={displayedLabel} queryValue={queryValue || ""} />
                // return <>
                //     {parts.map((part, index) =>
                //         part.toLowerCase() === queryValue?.toLowerCase() ? (
                //             <span key={index} style={{ backgroundColor: 'yellow' }}>{part}</span> // highlight style
                //         ) : (
                //             part
                //         )
                //     )}

                // </>
                // const displayedLabel = `${row.name_en}`

                // // console.log("row HGIHLIGHT ======>>", row._highlightResult);

                // const displayedHighlightedLabel = ` ${row._highlightResult?.name_en?.value}`

                // return <>
                //     {row._highlightResult && displayedHighlightedLabel ? (
                //         <span dangerouslySetInnerHTML={{ __html: displayedHighlightedLabel }} />
                //     ) : (
                //         <Text>{displayedLabel}</Text>
                //     )}
                // </>


                // <Highlight attribute={"name_en"} hit={row} />
            },
            // grow: true,
        },
        {
            name: 'Mobile',
            id: "mobile",
            selector: (row: any) => row.mobile,
            sortable: true,
            grow: 0.5,
            cell(row,) {

                const displayedLabel = `${row.mobile}`


                // const regex = new RegExp(`(${queryValue})`, 'gi'); // Case insensitive match
                // const fuzzyRegex = new RegExp(`(${queryValue?.split('').join('.*?')})`, 'gi'); // Matches characters in any order with any characters in between

                // const parts: string[] = displayedLabel?.split(fuzzyRegex) || [];
                return <HighlightedText displayedLabel={displayedLabel} queryValue={queryValue || ""} />

                // return <>
                //     {parts.map((part, index) =>
                //         part.toLowerCase() === queryValue?.toLowerCase() ? (
                //             <span key={index} style={{ backgroundColor: 'yellow' }}>{part}</span> // highlight style
                //         ) : (
                //             part
                //         )
                //     )}

                // </>
                // return <Highlight attribute={"mobile"} hit={row} />

            },
        },
        {
            name: 'ID',
            id: "civilID",
            selector: (row: any) => row.civilID,
            sortable: true,
            // grow: true,
            grow: 1,
            cell(row,) {

                let displayedLabel: string;
                // Other-ID clients show the document type so RMs can tell a passport from a national ID
                const otherIdLabel = row.other_id
                    ? `${row.other_id}${row.other_id_type ? ` (${OTHER_ID_TYPE_LABELS[row.other_id_type as OtherIdType] ?? row.other_id_type}${row.other_id_country ? ` · ${row.other_id_country}` : ""})` : ""}`
                    : undefined;
                if (row.customerType === "individual") {
                    displayedLabel = `${row.civilID ?? otherIdLabel ?? '-'}`
                } else if (row.customerType === "company") {
                    displayedLabel = `${row.commercial_register ?? '-'}`
                } else {
                    displayedLabel = `${row.civilID ?? otherIdLabel ?? row.commercial_register ?? '-'}`
                }


                // console.log("row HGIHLIGHT ======>>", row._highlightResult);

                // const displayedHighlightedLabel = ` ${row._highlightResult?.civilID?.value}`
                // const regex = new RegExp(`(${queryValue})`, 'gi'); // Case insensitive match
                // const fuzzyRegex = new RegExp(`(${queryValue?.split('').join('.*?')})`, 'gi'); // Matches characters in any order with any characters in between

                // const parts: string[] = displayedLabel?.split(fuzzyRegex) || [];
                return <HighlightedText displayedLabel={displayedLabel} queryValue={queryValue || ""} />

                // return <>
                //     {parts.map((part, index) =>
                //         part.toLowerCase() === queryValue?.toLowerCase() ? (
                //             <span key={index} style={{ backgroundColor: 'yellow' }}>{part}</span> // highlight style
                //         ) : (
                //             part
                //         )
                //     )}

                // </>
                // return <Highlight attribute={"civilID"} hit={row} />

            },
        },
        {
            name: 'Segment',
            grow: 1,
            // Start of Selection
            cell: (row: any) => {

                // Find the segment config for the current row
                const config = configContext.segments?.segmentList?.find(s => s.name === row.segment);

                if (config) {
                    return <Badge color={config.color} size="sm">{row.segment}</Badge>;
                }

                return <span></span>;
            }
        },
        {
            name: 'Tags',
            // Start of Selection
            cell: (row: any) => {

                return <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {Object.keys(row.tags).sort().map((tagKey, i) => {
                        let tag = row.tags[tagKey];
                        return <TagBadge tag={tag} key={`${tagKey}-${i}`} />
                    })}
                </div>
            }
            ,
            // grow: false,
            // grow: 0,
            wrap: true,
        },

        {
            id: "lastUnsubmittedKYCMessage.count",
            name: "Actions",
            cell: (row: any) => <ActionsComponent customerObj={row} />
            ,

            grow: 0,
            sortable: true

        }



    ];


    return <Grid justify='center' align='center'>


        <Grid.Col span={12}  >
            <Title size={"md"} my={"sm"}>
                Search
            </Title>
            <Group >

                <SearchBox

                    style={{
                        display: "flex",
                        justifyContent: "flex-end",

                        width: "90%",
                    }}
                    autoFocus
                    placeholder='Search...'


                />
                <Button onClick={() => setOpenFilterModal(true)}>
                    Filter
                </Button>
            </Group>

            <FilterBadgeGroup
                items={customerTypeItems}
                color="blue"
                refine={clearCustomerType}
                renderLabel={customerTypeFilterLabel}
            />
            <FilterBadgeGroup
                items={kycStatusItems}
                color="cyan"
                refine={refineKycStatus}
            />
            <FilterBadgeGroup
                items={tagsItems}
                color="grape"
                refine={refineTags}
            />
            <FilterBadgeGroup
                items={segmentItems}
                color="orange"
                refine={refineSegment}
            />
            <FilterBadgeGroup
                items={nationalityItems}
                color="violet"
                refine={refineNationality}
            />
            <FilterBadgeGroup
                items={consentItems}
                color="green"
                refine={refineConsent}
                renderLabel={consentFilterLabel}
            />


        </Grid.Col>



        <Grid.Col span={12} mt={0} >
            <DataTable
                key={clearTrigger}
                sortServer
                onSort={(column, sortDirection) => {
                    if (column.id) {
                        let sortByValue = `${indexName}/sort/${column.id}`
                        if (sortDirection === "asc") {
                            sortByValue += `:asc`
                        }
                        else {
                            sortByValue += `:desc`
                        }

                        refine(sortByValue)
                    }
                }}
                columns={columns}
                selectableRows
                onSelectedRowsChange={handleChange}
                data={items.map((hitObj) => {
                    return { ...hitObj, customerID: hitObj.id }
                })}
            // conditionalRowStyles={conditionalRowStyles}
            />
        </Grid.Col>
        <FilterModal
            opened={openFilterModal}
            onClose={() => setOpenFilterModal(false)}
            sections={[
                {
                    id: 'customerType',
                    title: 'Filter by customer type',
                    type: 'custom',
                    selectedCount: customerType === "all" ? 0 : 1,
                    onClear: clearCustomerType,
                    renderContent: () => (
                        <Select
                            aria-label="Customer type"
                            data={[
                                { value: "all", label: "All" },
                                { value: "individual", label: "Individual" },
                                { value: "company", label: "Company" },
                                { value: "trust", label: "Trust" },
                            ]}
                            value={customerType}
                            onChange={(value) => setCustomerType(value ?? "all")}
                            allowDeselect={false}
                        />
                    ),
                },
                {
                    id: 'kycStatus',
                    title: 'Filter by KYC Status',
                    items: kycStatusItems,
                    refine: refineKycStatus,
                    renderLabel: (item) => item.label,
                },
                {
                    id: 'tags',
                    title: 'Filter by tags',
                    items: tagsItems,
                    refine: refineTags,
                    renderLabel: (item) => {
                        const fundMap = dataContext.fundsMap;
                        return (
                            <>
                                {fundMap && fundMap[item.value]
                                    ? (fundMap[item.value]?.name_en) || fundMap[item.value]?.name || fundMap[item.value].code
                                    : item.label}
                            </>
                        );
                    },
                },
                {
                    id: 'segment',
                    title: 'Filter by segment',
                    items: segmentItems,
                    refine: refineSegment,
                    renderLabel: (item) => item.label,
                },
                {
                    id: 'nationality',
                    title: 'Filter by Nationality',
                    items: nationalityItems,
                    refine: refineNationality,
                    renderLabel: (item) => item.label,
                },
                {
                    id: 'consent',
                    title: 'Filter by consent agreement',
                    items: consentItems,
                    refine: refineConsent,
                    renderLabel: consentFilterLabel,
                },
            ] as FilterSection[]}
        />
    </Grid>

}

interface CustomerInstantSearchProps {
    indexName: string,
    setSelectedRows?: any
    renderTags?: boolean
    ActionsComponent?: any
    navigateDetails?: any
    selectable?: boolean
    selectedRows?: any[]
    topElements?: JSX.Element,
    showTopElements?: boolean
    clearTrigger?: number
}
const CustomerInstantSearch = ({ indexName, selectable, setSelectedRows, selectedRows = [], navigateDetails, renderTags, ActionsComponent, topElements, clearTrigger }: CustomerInstantSearchProps) => {
    const [orderStatusFilter, setCustomerKYCStatusFilter] = useState<string | null>("")
    const [customerType, setCustomerType] = useState<Exclude<CustomerType, "other"> | "all">("all");
    const [totalCustomers, setTotalCustomers] = useState<number | undefined>()

    // Callback function to update total customers
    const handleTotalCustomersUpdate = (count: number) => {
        setTotalCustomers(count);
    };

    const userData = useContext(MyUserDataContext);
    let customClaims = userData.firebaseTokenResult?.claims.customClaims as any;

    const handleRowsToShow = () => {


        const kycStatuses: string[] = []


        if (customClaims["cr"] === true) {
            kycStatuses.push(...[KYCStatusCode.review, KYCStatusCode.approval, KYCStatusCode.exec_approval])

        }


        if (kycStatuses.length) {
            if (kycStatuses.length === 1) {
                setCustomerKYCStatusFilter(`tags.kycStatus.code:${kycStatuses[0]}`)

            }
            else {

                setCustomerKYCStatusFilter(`tags.kycStatus.code:[${kycStatuses.join()}]`)
            }
        }
        else {
            setCustomerKYCStatusFilter(null)
        }

    }
    const typesenseInstantsearchAdapter: TypesenseInstantsearchAdapter = new TypesenseInstantSearchAdapter({
        server: {
            apiKey: userData.customerCollectionSearchKey!, // Be sure to use a Search API Key
            nodes: [
                {
                    host: import.meta.env.VITE_TYPESENSE_SEARCH_HOST!,
                    port: 443,
                    protocol: 'https'
                },
            ],

        },

        additionalSearchParameters: {

            query_by: "segment,name_en,name_ar,mobile,civilID,commercial_register,portfolioKeys,tags,crmID",
            facet_by: "*"
            // prioritize_exact_match: true,
            // enable_highlight_v1: true,
            // text_match_type: "max_score",
        }
        ,


    })

    let typesenseSearchClient: SearchClient = typesenseInstantsearchAdapter.searchClient as SearchClient;


    if (!indexName) {
        indexName = import.meta.env.VITE_TYPESENSE_CUSTOMERS_COLLECTION!;
    }

    const searchStateScope = `${userData.user?.uid ?? 'anon'}_${(userData.customerCollectionSearchKey ?? '').slice(0, 12)}`;

    //save to local session
    const [initialUIStateForIndex, setInitialUIStateForIndex] = useSessionStorage<InitialState>({
        key: `initialUIStateFor_${indexName}_${searchStateScope}`,
        getInitialValueInEffect: false,
    });


    const onStateChange: InstantSearchProps['onStateChange'] = ({
        uiState,
        setUiState,
    }) => {
        // Custom logic
        // setUiState(uiState);
        // console.log("setting ui state", uiState);
        setInitialUIStateForIndex(uiState[indexName]);

    };

    // console.log("session for ", indexName, initialUIStateForIndex);

    // if (!initialUIStateForIndex)  {
    //     return <div></div>;
    // }
    return (
        <InstantSearch

            searchClient={typesenseSearchClient}
            indexName={indexName}
            initialUiState={{
                [indexName]: initialUIStateForIndex
            }}
            onStateChange={onStateChange}

        >
            <Configure

                filters={[
                    orderStatusFilter,
                    customerType === "all" ? "" : `customerType:=${customerType}`,
                ].filter(Boolean).join(" && ")}
                exactOnSingleWordQuery='attribute'
            // query={"q=*&filter_by=lastUnsubmittedKYCMessage.count:>=4"}

            />




            <Grid >
                <Grid.Col span={7}>
                    {topElements}
                </Grid.Col>
                <Grid.Col span={4}>
                    <Group justify="space-between">
                        <Checkbox

                            label="Show all customers"
                            defaultChecked={true}
                            onChange={(event) => {
                                if (!event.currentTarget.checked) {
                                    handleRowsToShow()
                                }
                                else {
                                    setCustomerKYCStatusFilter(null)
                                }
                            }

                            }
                        />
                        <RefreshButton />
                    </Group>
                </Grid.Col>


            </Grid>
            {/* <Text>Index: {import.meta.env.VITE_TYPESENSE_CUSTOMERS_COLLECTION}</Text> */}


            <TableDisplay
                indexName={indexName}
                queryValue={initialUIStateForIndex?.query || ""}
                selectedRows={selectedRows}
                setSelectedRows={selectable ? setSelectedRows : undefined}
                navigateDetails={navigateDetails}
                ActionsComponent={ActionsComponent}
                renderTags={renderTags}
                onTotalCustomersUpdate={handleTotalCustomersUpdate}
                clearTrigger={clearTrigger}
                customerType={customerType}
                setCustomerType={setCustomerType}

            />

            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",

                    width: "100%",
                }}
            >
                <HitsPerPage
                    items={[
                        { label: '250', value: 250, },
                        { label: '100', value: 100 },
                        { label: '50', value: 50, default: true },
                    ]}
                />
            </div>
            <Space h="xl" />
            <Pagination
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    width: "100%",


                }}
            />
            {totalCustomers && <Text mt={10} ta={'end'} style={{
                'color': '#23263b',
                'fontSize': '0.875rem',
                'fontWeight': '400'
            }}>Total Number of Customers: {totalCustomers}</Text>}

        </InstantSearch >



    )
}

export default CustomerInstantSearch
